import type { FrontendApi, UiNodeInputAttributes } from '@ory/kratos-client';
import axios, { type AxiosError } from 'axios';
import mixpanel from 'mixpanel-browser';

import { oryClient } from '@revelio/auth';
import { CTAInteractionTrackingEvents } from '@revelio/core';

import { environment } from '../../environment';
import type { FormValuesPassRequired } from '../components/models';
import { isE2eTestSignupEmail } from './is-e2e-test-signup-email';

const isProduction = window.location.host.startsWith('account.reveliolabs.com');

export const createUnverifiedUser = async ({
  body,
  csrfToken,
}: {
  body: FormValuesPassRequired;
  csrfToken: string;
}) => {
  const { email } = body;

  const createUserResp = await axios
    .post(`${environment.PUBLIC_USER_MGMT_URL}/api/users/create`, body, {
      timeout: 0,
      headers: {
        'x-surf-token': csrfToken,
      },
      withCredentials: true,
      xsrfCookieName: 'x-surf-token',
      xsrfHeaderName: 'x-surf-token',
    })
    .catch((error: AxiosError<{ message: string; userId: string }>) => {
      console.log('error:', error);
      if (error.response?.status == 424) {
        return { data: { userId: error.response.data.userId } };
      }

      throw error;
    });

  const orySdk = oryClient as FrontendApi;
  const flowInit = await orySdk.createBrowserVerificationFlow();
  const csrfValueNode = flowInit.data.ui.nodes.find(
    (n) => (n.attributes as UiNodeInputAttributes).name == 'csrf_token'
  );
  const firstOryCsrf = (csrfValueNode?.attributes as UiNodeInputAttributes)
    .value;
  const flowPickMethod = await orySdk.updateVerificationFlow({
    flow: flowInit?.data.id,
    updateVerificationFlowBody: {
      method: 'code',
      csrf_token: firstOryCsrf,
      email,
    },
  });
  const csrfValueNodeSecond = flowPickMethod.data.ui.nodes.find(
    (n) => (n.attributes as UiNodeInputAttributes).name == 'csrf_token'
  );
  const oryCsrf = (csrfValueNodeSecond?.attributes as UiNodeInputAttributes)
    .value;

  // Skip the prod Mixpanel event for the e2e signup test so test runs
  // don't pollute analytics.
  if (isProduction && !isE2eTestSignupEmail(email)) {
    mixpanel.track(CTAInteractionTrackingEvents.SIGN_UP_SUCCESS, {
      source: 'dashboard',
    });
  }

  return {
    oryCsrf,
    flowId: flowInit?.data?.id,
    revelioId: createUserResp.data?.userId,
  };
};
